import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, Plus, AlertCircle, Grid3x3, List, Clock, CheckCircle2, Archive, Settings } from 'lucide-react';
import { OnboardingProject, OnboardingStatus } from '@/types/onboarding';
import { onboardingApi } from './services/onboardingApi';
import { useToast } from '@/hooks/use-toast';
import OnboardingProjectCard from './components/OnboardingProjectCard';
import { SkeletonProjectList } from './components/SkeletonCard';
import E2ETestPanel from './components/E2ETestPanel';

type StatusFilter = 'in_progress' | 'completed' | 'archived';

const OnboardingModule = () => {
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const [projects, setProjects] = useState<OnboardingProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('in_progress');
  const [importedCountsMap, setImportedCountsMap] = useState<Record<string, { properties: number; units: number; accounts: number }>>({});
  const [statusCounts, setStatusCounts] = useState({ in_progress: 0, completed: 0, archived: 0 });

  useEffect(() => {
    if (currentWorkspace) {
      loadProjects(statusFilter);
      loadStatusCounts();
    }
  }, [currentWorkspace, statusFilter]);

  const loadStatusCounts = async () => {
    if (!currentWorkspace) return;
    try {
      const [inProgress, completed, archived] = await Promise.all([
        onboardingApi.listProjects(currentWorkspace.id, 'in_progress'),
        onboardingApi.listProjects(currentWorkspace.id, 'completed'),
        onboardingApi.listProjects(currentWorkspace.id, 'archived'),
      ]);
      setStatusCounts({
        in_progress: inProgress.projects.length,
        completed: completed.projects.length,
        archived: archived.projects.length,
      });
    } catch (error) {
      console.error('Failed to load status counts:', error);
    }
  };

  const loadProjects = async (filter: StatusFilter) => {
    if (!currentWorkspace) return;

    try {
      setLoading(true);
      const data = await onboardingApi.listProjects(currentWorkspace.id, filter);
      const projectsList = data.projects || [];
      setProjects(projectsList);
      
      const countsMap: Record<string, { properties: number; units: number; accounts: number }> = {};
      if (projectsList.length > 0) {
        await Promise.all(
          projectsList.map(async (project) => {
            try {
              const counts = await onboardingApi.getImportedCounts(project.id);
              countsMap[project.id] = counts;
            } catch {
              countsMap[project.id] = { properties: 0, units: 0, accounts: 0 };
            }
          })
        );
      }
      setImportedCountsMap(countsMap);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = () => {
    navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/new/stage-1`);
  };

  const handleViewProject = (projectId: string) => {
    navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-1`);
  };

  const handleArchiveProject = async (projectId: string) => {
    try {
      await onboardingApi.archiveProject(projectId);
      toast({ title: 'Project archived', description: 'Customer has been moved to archived' });
      loadProjects(statusFilter);
      loadStatusCounts();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleUnarchiveProject = async (projectId: string) => {
    try {
      await onboardingApi.unarchiveProject(projectId);
      toast({ title: 'Project restored', description: 'Customer has been restored' });
      loadProjects(statusFilter);
      loadStatusCounts();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDeleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to permanently delete this customer and all associated data?')) return;
    try {
      await onboardingApi.deleteProject(projectId);
      toast({ title: 'Project deleted', description: 'Customer has been permanently deleted' });
      loadProjects(statusFilter);
      loadStatusCounts();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const formatStatus = (status: OnboardingStatus) => {
    const statusMap: Record<OnboardingStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
      draft: { label: 'Draft', variant: 'outline' },
      in_progress: { label: 'In Progress', variant: 'default' },
      pending_approval: { label: 'Pending Approval', variant: 'secondary' },
      approved: { label: 'Approved', variant: 'default' },
      live: { label: 'Live', variant: 'default' },
      cancelled: { label: 'Cancelled', variant: 'destructive' },
    };
    return statusMap[status] || { label: status, variant: 'outline' };
  };

  const formatStage = (stage: string) => {
    const stageMap: Record<string, string> = {
      initiated: 'Customer Initiation',
      data_import: 'Data Import',
      validation: 'Validation',
      mapping: 'Mapping',
      configuration: 'Configuration',
      review: 'Review',
      go_live: 'Go-Live',
      completed: 'Completed',
    };
    return stageMap[stage] || stage;
  };

  if (loading && projects.length === 0) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <PageHeader title="Onboarding" description="Bulk import properties, units, tenants, accounts, and bills" />
        <SkeletonProjectList count={3} />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader title="Onboarding" description="Bulk import properties, units, tenants, accounts, and bills" />

      {/* Header with Create Button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Onboarding Customers</h2>
          <p className="text-muted-foreground mt-1">Manage customer onboarding workflows</p>
        </div>
        <div className="flex gap-2">
          <div className="flex items-center gap-1 border rounded-md p-1">
            <Button variant={viewMode === 'grid' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('grid')} className="h-8">
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button variant={viewMode === 'list' ? 'default' : 'ghost'} size="sm" onClick={() => setViewMode('list')} className="h-8">
              <List className="h-4 w-4" />
            </Button>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/change-management`)}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            Change Management
          </Button>
          <Button onClick={handleCreateProject} className="gap-2">
            <Plus className="h-4 w-4" />
            New Customer
          </Button>
        </div>
      </div>

      {/* Status Tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="in_progress" className="gap-2">
            <Clock className="h-4 w-4" />
            In Progress
            {statusCounts.in_progress > 0 && (
              <Badge variant="secondary" className="ml-1">{statusCounts.in_progress}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="completed" className="gap-2">
            <CheckCircle2 className="h-4 w-4" />
            Completed
            {statusCounts.completed > 0 && (
              <Badge variant="secondary" className="ml-1">{statusCounts.completed}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="archived" className="gap-2">
            <Archive className="h-4 w-4" />
            Archived
            {statusCounts.archived > 0 && (
              <Badge variant="secondary" className="ml-1">{statusCounts.archived}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value={statusFilter} className="mt-6">
          {projects.length === 0 ? (
            <EmptyState filter={statusFilter} onCreate={handleCreateProject} />
          ) : (
            <ProjectsList 
              projects={projects} 
              onViewProject={handleViewProject}
              onArchive={handleArchiveProject}
              onUnarchive={handleUnarchiveProject}
              onDelete={handleDeleteProject}
              formatStatus={formatStatus}
              formatStage={formatStage}
              viewMode={viewMode}
              importedCountsMap={importedCountsMap}
              isArchived={statusFilter === 'archived'}
            />
          )}
        </TabsContent>
      </Tabs>

      <E2ETestPanel />
    </div>
  );
};

const EmptyState = ({ filter, onCreate }: { filter: StatusFilter; onCreate: () => void }) => {
  const messages = {
    in_progress: { title: 'No customers in progress', description: 'Start a new onboarding to begin' },
    completed: { title: 'No completed customers', description: 'Customers that finish onboarding will appear here' },
    archived: { title: 'No archived customers', description: 'Archived customers will appear here' },
  };
  const msg = messages[filter];

  return (
    <Card className="p-12 text-center">
      <div className="flex flex-col items-center space-y-4">
        <div className="h-20 w-20 rounded-full bg-muted flex items-center justify-center">
          <Upload className="h-10 w-10 text-muted-foreground" />
        </div>
        <h3 className="text-2xl font-semibold">{msg.title}</h3>
        <p className="text-muted-foreground max-w-md">{msg.description}</p>
        {filter === 'in_progress' && (
          <Button onClick={onCreate} className="mt-4">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Customer
          </Button>
        )}
      </div>
    </Card>
  );
};

interface ProjectsListProps {
  projects: OnboardingProject[];
  onViewProject: (id: string) => void;
  onArchive: (id: string) => void;
  onUnarchive: (id: string) => void;
  onDelete: (id: string) => void;
  formatStatus: (status: OnboardingStatus) => { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' };
  formatStage: (stage: string) => string;
  viewMode: 'grid' | 'list';
  importedCountsMap: Record<string, { properties: number; units: number; accounts: number }>;
  isArchived: boolean;
}

const ProjectsList = ({ 
  projects, 
  onViewProject,
  onArchive,
  onUnarchive,
  onDelete,
  formatStatus,
  formatStage,
  viewMode,
  importedCountsMap,
  isArchived
}: ProjectsListProps) => {
  return (
    <div className={viewMode === 'grid' 
      ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      : "space-y-4"
    }>
      {projects.map(project => (
        <OnboardingProjectCard
          key={project.id}
          project={project}
          onView={onViewProject}
          onArchive={onArchive}
          onUnarchive={onUnarchive}
          onDelete={onDelete}
          formatStatus={formatStatus}
          formatStage={formatStage}
          importedCounts={importedCountsMap[project.id] || { properties: 0, units: 0, accounts: 0 }}
          isArchived={isArchived}
        />
      ))}
    </div>
  );
};

export default OnboardingModule;
